import { useCallback, useEffect, useRef, useState } from 'react';
import {
    fetchNotifications,
    mapNotification,
} from '../services/notifications/notificationService';
import {
    useNotificationsContext,
    POLL_INTERVAL_MS,
} from '../contexts/NotificationsContext';

/**
 * Shared hook that powers both the bell widget and the notifications page.
 *
 * - Issues a single API call shape via {@link fetchNotifications} using the
 *   caller-supplied filter/pagination params.
 * - Polls every {@link POLL_INTERVAL_MS} milliseconds, pausing while the
 *   document is hidden or while `paused` is true (e.g. a dialog or popover
 *   is open).
 * - Subscribes to {@link NotificationsContext.activeCountVersion}. Whenever
 *   the active count changes, the list is refetched immediately instead of
 *   waiting for the next polling tick — this is what keeps the widget list
 *   and the page in sync with the bell badge count.
 *
 * @param {Object} [options]
 * @param {Object} [options.params]  Params forwarded to `fetchNotifications`
 *                                   (siteId, severity, state, take, ct, ...).
 * @param {boolean} [options.enabled=true]  When false no polling runs.
 * @param {boolean} [options.paused=false]  Temporarily pause the periodic
 *                                   timer (the hook still refetches on
 *                                   active-count changes and when `paused`
 *                                   flips back to false).
 * @returns {{
 *   items: Array,
 *   continuationToken: string | null,
 *   loading: boolean,
 *   initialLoading: boolean,
 *   error: string | null,
 *   refresh: () => Promise<void>,
 * }}
 */
export const useNotificationsList = (options = {}) => {
    const { params, enabled: requestedEnabled = true, paused = false } = options;
    const { activeCountVersion, callerKey, requestsEnabled = true } = useNotificationsContext();
    const enabled = requestedEnabled && requestsEnabled;

    const [items, setItems] = useState([]);
    const [page, setPage] = useState({ totalCount: 0, nextCursor: null, previousCursor: null, snapshotAt: null, facets: {} });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    const [silencingUnavailable, setSilencingUnavailable] = useState(false);
    const [resultKey, setResultKey] = useState(null);

    // Serialize params so the effect dependency is a stable primitive.
    const paramsKey = JSON.stringify(params || {});
    const scopeKey = JSON.stringify([paramsKey, callerKey, activeCountVersion, enabled]);
    const scopeRef = useRef(scopeKey);
    scopeRef.current = scopeKey;
    const paramsRef = useRef(params);
    paramsRef.current = params;

    const intervalRef = useRef(null);
    const cancelledRef = useRef(false);
    const requestRef = useRef(0);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!enabled) return;
        const scope = scopeRef.current;
        const request = ++requestRef.current;
        const isCurrent = () => !cancelledRef.current && scopeRef.current === scope && requestRef.current === request;
        if (!silent) setLoading(true);
        setError(null);
        try {
            const response = await fetchNotifications(paramsRef.current || {});
            if (!isCurrent()) return;
            const mapped = (response?.items || []).map(mapNotification);
            setItems(mapped);
            setResultKey(scope);
            setSilencingUnavailable(Boolean(response?.silencingUnavailable));
            setPage({
                totalCount: response?.totalCount || 0,
                nextCursor: response?.nextCursor || null,
                previousCursor: response?.previousCursor || null,
                snapshotAt: response?.snapshotAt || null,
                facets: response?.facets || {},
            });
        } catch (err) {
            if (isCurrent()) {
                setResultKey(scope);
                setError(err?.message || 'Failed to load notifications');
            }
        } finally {
            if (isCurrent()) {
                setLoading(false);
                setInitialLoading(false);
            }
        }
    }, [enabled]);

    // Mount / params change: reset state and trigger an initial load.
    useEffect(() => {
        cancelledRef.current = false;
        setInitialLoading(true);
        setItems([]);
        setPage({ totalCount: 0, nextCursor: null, previousCursor: null, facets: {} });
        setSilencingUnavailable(false);
        load();
        return () => {
            cancelledRef.current = true;
        };
    }, [scopeKey, enabled, load]);

    // Periodic polling — can be paused without tearing down the subscription.
    useEffect(() => {
        if (!enabled || paused) return undefined;

        const start = () => {
            if (intervalRef.current) return;
            intervalRef.current = setInterval(() => {
                if (typeof document !== 'undefined' && document.hidden) return;
                load({ silent: true });
            }, POLL_INTERVAL_MS);
        };
        const stop = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        start();

        const onVisibilityChange = () => {
            if (typeof document === 'undefined') return;
            if (document.hidden) {
                stop();
            } else {
                load({ silent: true });
                start();
            }
        };
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onVisibilityChange);
        }

        return () => {
            stop();
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', onVisibilityChange);
            }
        };
    }, [enabled, paused, load]);

    const refresh = useCallback(() => load({ silent: false }), [load]);

    return {
        items: resultKey === scopeKey ? items : [],
        ...(resultKey === scopeKey ? page : { totalCount: 0, nextCursor: null, previousCursor: null, facets: {} }),
        loading,
        initialLoading: resultKey !== scopeKey || initialLoading,
        error: resultKey === scopeKey ? error : null,
        silencingUnavailable: resultKey === scopeKey && silencingUnavailable,
        refresh,
    };
};
